import {
  VITE_AUTH_BOOT_HOLD_SHARE,
  VITE_AUTH_BOOT_HOLD_DEADLINE_MS,
} from 'src/constants/envs';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { readStoredString } from 'src/utils/storage';
import { SESSION_EVENTS, type tSessionEventProperties } from 'src/hooks/analytics/sessionAnalytics.constants';

export type tBootArm = 'hold' | 'control';
export type tBootOutcome =
  | 'live' | 'held_live' | 'mirror' | 'control_3s' | 'ineligible_3s'
  | 'deadline' | 'sdk_unavailable' | 'cross_account'
  | 'network_refresh_failed' | 'signed_out' | 'refresh_rejected';
export type tTokenlessAuthOutcome = tBootOutcome | 'explicit_empty_authorization' | 'unknown';

type tBootOptions = { random?: () => number; share?: number; deadlineMs?: number };

const DOCUMENT_ORDINAL_KEY = 'authBootDocumentOrdinal';

const createBootSession = (options: tBootOptions = {}) => {
  const share = options.share ?? VITE_AUTH_BOOT_HOLD_SHARE;
  let ordinal = 1;
  try {
    const previous = Number(sessionStorage.getItem(DOCUMENT_ORDINAL_KEY));
    ordinal = Number.isSafeInteger(previous) && previous > 0 ? previous + 1 : 1;
    sessionStorage.setItem(DOCUMENT_ORDINAL_KEY, String(ordinal));
  } catch { /* Storage can be blocked; the document still boots. */ }
  const navigation = performance.getEntriesByType?.('navigation')[0] as
    (PerformanceNavigationTiming & { activationStart?: number }) | undefined;
  return {
    bootId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    arm: ((options.random ?? Math.random)() < share ? 'hold' : 'control') as tBootArm,
    share,
    deadlineMs: options.deadlineMs ?? VITE_AUTH_BOOT_HOLD_DEADLINE_MS,
    workspaceId: readStoredString('currentWorkspaceId'),
    navigationType: navigation?.type ?? 'unknown',
    prerendered: (navigation?.activationStart ?? 0) > 0,
    tabDocumentOrdinal: ordinal,
    openedAt: undefined as number | undefined,
    savedUid: undefined as string | undefined,
    eligible: false,
    cohort: false,
    heldAt: undefined as number | undefined,
    wasHiddenDuringHold: false,
    settledAt: undefined as number | undefined,
    settledWithUser: undefined as boolean | undefined,
    mirrorState: undefined as 'absent' | 'expired' | 'unreadable' | undefined,
    crossAccountDetected: false,
    outcome: undefined as tBootOutcome | undefined,
    retryCount: 0,
    tokenlessRequests: 0,
    tokenlessRequestsByAuthOutcome: {} as Partial<Record<tTokenlessAuthOutcome, number>>,
    firstTokenlessRequest: undefined as { at: number; outcome: tTokenlessAuthOutcome } | undefined,
    degraded401s: 0,
    emitted: new Set<string>(),
  };
};

let session = createBootSession();
let stopObservingVisibility: (() => void) | undefined;

export const getBootSession = () => session;

export const recordTokenlessRequest = (outcome: tTokenlessAuthOutcome) => {
  session.tokenlessRequests += 1;
  session.tokenlessRequestsByAuthOutcome[outcome] = (session.tokenlessRequestsByAuthOutcome[outcome] ?? 0) + 1;
  session.firstTokenlessRequest ??= { at: performance.now(), outcome };
};

const tokenlessRequestDiagnostics = () => ({
  request_diagnostics_version: 1 as const,
  tokenless_requests_by_auth_outcome: { ...session.tokenlessRequestsByAuthOutcome },
  ...(session.firstTokenlessRequest ? {
    first_tokenless_request_ms_since_navigation_start: session.firstTokenlessRequest.at,
    first_tokenless_request_auth_outcome: session.firstTokenlessRequest.outcome,
  } : {}),
});

export const openBootWindow = () => {
  if (session.openedAt === undefined) {
    session.openedAt = performance.now();
    session.savedUid = readStoredString('currentUserId');
    session.eligible = Boolean(session.savedUid);
  }
  return session;
};

export const getBootProperties = () => ({
  boot_id: session.bootId,
  auth_hold_arm: session.arm,
});

// This seam must never prevent a request, cleanup or redirect from finishing.
// Properties are explicit per event: register() would persist them across boots.
export const emitBootEvent = (event: string, properties: Record<string, unknown> = {}) => {
  try {
    getAnalytics().track(event, {
      ...properties,
      ...getBootProperties(),
      hold_share: session.share,
      deadline_ms: session.deadlineMs,
      ...(session.workspaceId ? { workspace_id: session.workspaceId } : {}),
    });
  } catch { /* Analytics is best effort. */ }
};

const emitOnce = <T extends keyof tSessionEventProperties>(event: T, properties: tSessionEventProperties[T]) => {
  if (session.emitted.has(event)) return;
  session.emitted.add(event);
  emitBootEvent(event, properties);
};

export const recordBootAuthPending = () => {
  if (!session.cohort || !session.mirrorState || session.emitted.has(SESSION_EVENTS.BOOT_AUTH_PENDING)) return;
  if (session.arm === 'hold') {
    session.heldAt ??= performance.now();
    const observe = () => { session.wasHiddenDuringHold ||= document.visibilityState === 'hidden'; };
    observe();
    document.addEventListener('visibilitychange', observe);
    stopObservingVisibility = () => document.removeEventListener('visibilitychange', observe);
  }
  const route = window.location.pathname?.split('/')[1];
  emitOnce(SESSION_EVENTS.BOOT_AUTH_PENDING, {
    mirror_state: session.mirrorState,
    navigation_type: session.navigationType,
    prerendered: session.prerendered,
    tab_document_ordinal: session.tabDocumentOrdinal,
    visibility_state: document.visibilityState,
    route_group: route && ['auth', 'dashboard', 'workspace', 'settings', 'onboarding'].includes(route) ? route : 'other',
    ms_since_navigation_start: performance.now(),
  });
};

export const recordBootAuthResolved = (outcome: tBootOutcome) => {
  if (!session.cohort || session.emitted.has(SESSION_EVENTS.BOOT_AUTH_RESOLVED)) return;
  stopObservingVisibility?.();
  stopObservingVisibility = undefined;
  emitOnce(SESSION_EVENTS.BOOT_AUTH_RESOLVED, {
    outcome,
    held_ms: session.heldAt === undefined ? 0 : performance.now() - session.heldAt,
    visibility_state: document.visibilityState,
    was_hidden_during_hold: session.wasHiddenDuringHold,
  });
};

const firebaseCalls = () => {
  try {
    return performance.getEntriesByType('resource').flatMap((entry) => {
      const host = new URL(entry.name).hostname;
      return ['securetoken.googleapis.com', 'identitytoolkit.googleapis.com', 'apis.google.com'].includes(host)
        ? [{ host, duration: entry.duration }] : [];
    });
  } catch { return []; }
};

export const recordBootAuthSettled = () => {
  if (!session.cohort || session.settledAt === undefined || session.openedAt === undefined) return;
  emitOnce(SESSION_EVENTS.BOOT_AUTH_SETTLED, {
    settle_ms: session.settledAt - session.openedAt,
    settled_with_user: Boolean(session.settledWithUser),
    firebase_calls: firebaseCalls(),
    tokenless_requests: session.tokenlessRequests,
    degraded_401s: session.degraded401s,
    ...tokenlessRequestDiagnostics(),
  });
};

export const recordBootDataReady = (attempts: number) => {
  if (!session.cohort) return;
  emitOnce(SESSION_EVENTS.BOOT_DATA_READY, {
    ms_since_navigation_start: performance.now(), attempts,
    tokenless_requests: session.tokenlessRequests, degraded_401s: session.degraded401s,
    ...tokenlessRequestDiagnostics(),
  });
};

const FORCED_SIGN_OUT_ENDPOINTS = [
  '/workspace/:id', '/workspace/:id/feature-flags', '/workspace/:id/integrations',
  '/workspace/:id/subscription', '/feature-flags', '/integrations',
  '/subscription', '/subscription/summary',
  '/auth/session-touch', '/auth/session-check', '/auth/log-out',
];

const forcedSignOutEndpoint = (url?: string) => {
  try {
    const path = new URL(url ?? '', 'https://localhost').pathname;
    for (const template of FORCED_SIGN_OUT_ENDPOINTS) {
      const match = path.match(new RegExp(`^${template.replace(':id', '([^/]+)')}$`));
      if (match) return template.replace(':id', /^\d+$/.test(match[1] ?? '') ? ':n' : ':id');
    }
  } catch { /* Malformed or unknown routes carry no diagnostic path. */ }
  return 'unknown';
};

export const recordForcedSignOut = (options: { carriedToken: boolean; url?: string }) => {
  // Emit only finite route templates: arbitrary path segments can contain PII.
  const endpoint = forcedSignOutEndpoint(options.url);
  emitOnce(SESSION_EVENTS.FORCED_SIGN_OUT, {
    carried_token: options.carriedToken,
    firebase_sign_out_called: options.carriedToken,
    endpoint, tokenless_requests: session.tokenlessRequests, degraded_401s: session.degraded401s,
    ...tokenlessRequestDiagnostics(),
  });
};

export const resetBootSessionForTests = (options: tBootOptions = {}) => {
  stopObservingVisibility?.();
  stopObservingVisibility = undefined;
  session = createBootSession(options);
};
